import { useState, useId, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Receipt,
  CreditCard,
  CheckCircle2,
  Plus,
  Trash2,
  Loader2,
} from 'lucide-react';
import { SlideOver } from '@/components/ui/SlideOver';
import { StepIndicator } from '@/components/ui/StepIndicator';
import { Button } from '@/components/ui/Button';
import { PrintBillButton } from '@/components/ui/PrintBillButton';
import { BillPreviewModal } from '@/components/ui/BillPreviewModal';
import { useCheckOutBooking } from '@/hooks/api/useBookings';
import { useFolioSummary, useAddFolioItem, useRemoveFolioItem, useRecordPayment, usePrintBill } from '@/hooks/api/useFolio';
import type { BookingEntry } from '@/services/api/frontdesk.api';

// ─── Constants ───────────────────────────────────────────────────────────────

const STEPS = [
  { label: 'Folio Review' },
  { label: 'Payment' },
  { label: 'Confirm' },
];

const CHARGE_CATEGORIES = [
  { value: 'room', label: 'Room Charges' },
  { value: 'minibar', label: 'Minibar' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'damage', label: 'Damage' },
  { value: 'parking', label: 'Parking' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'other', label: 'Other' },
] as const;

type PaymentMethodValue = typeof PAYMENT_METHODS[number]['value'];

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

function fmtCurrency(amount: number | string, currency = 'GBP'): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(num);
}

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface NewChargeForm {
  category: string;
  description: string;
  amount: string;
  date: string;
}

interface PaymentForm {
  amount: string;
  method: PaymentMethodValue;
  reference: string;
  notes: string;
}

interface CheckOutWizardProps {
  isOpen: boolean;
  onClose: () => void;
  booking: BookingEntry;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CheckOutWizard({ isOpen, onClose, booking }: CheckOutWizardProps) {
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddCharge, setShowAddCharge] = useState(false);
  const [checkoutNotes, setCheckoutNotes] = useState('');
  // Actual checkout date — defaults to today (supports early checkout by picking an earlier date)
  const [actualCheckOutDate, setActualCheckOutDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  const [newCharge, setNewCharge] = useState<NewChargeForm>({
    category: 'minibar',
    description: '',
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd'),
  });

  const [payment, setPayment] = useState<PaymentForm>({
    amount: '0',
    method: 'cash',
    reference: '',
    notes: '',
  });

  // IDs for accessibility
  const chargeCategoryId = useId();
  const chargeDescId = useId();
  const chargeAmountId = useId();
  const chargeDateId = useId();
  const payAmountId = useId();
  const payMethodId = useId();
  const payRefId = useId();
  const payNotesId = useId();
  const checkoutNotesId = useId();

  // API hooks
  const folioQuery = useFolioSummary(isOpen ? booking.id : null);
  const addFolioItemMutation = useAddFolioItem();
  const removeFolioItemMutation = useRemoveFolioItem();
  const recordPaymentMutation = useRecordPayment();
  const checkOutMutation = useCheckOutBooking();
  const { openBill, isPrinting: isPrintingBill, preview: billPreview, closePreview } = usePrintBill();

  const folio = folioQuery.data;
  const totalCharges = folio?.totalCharges ?? (parseFloat(booking.totalAmount) || 0);
  const totalPayments = folio?.totalPayments ?? 0;
  const balance = folio?.balance ?? totalCharges;
  const currency = folio?.currency ?? booking.currency ?? 'GBP';

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setError('');
      setSuccessMessage('');
      setShowAddCharge(false);
      setCheckoutNotes('');
      setActualCheckOutDate(format(new Date(), 'yyyy-MM-dd'));
      setNewCharge({
        category: 'minibar',
        description: '',
        amount: '',
        date: format(new Date(), 'yyyy-MM-dd'),
      });
    }
  }, [isOpen]);

  // Update payment amount when folio loads
  useEffect(() => {
    if (folio && balance > 0) {
      setPayment((prev) => ({ ...prev, amount: balance.toFixed(2) }));
    }
  }, [folio, balance]);

  const guestName = booking.guest?.fullName ?? 'Unknown Guest';

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleAddCharge = async () => {
    if (!newCharge.description.trim() || !newCharge.amount) {
      setError('Please provide description and amount for the charge.');
      return;
    }
    setError('');
    try {
      await addFolioItemMutation.mutateAsync({
        bookingId: booking.id,
        payload: {
          category: newCharge.category,
          description: newCharge.description.trim(),
          amount: parseFloat(newCharge.amount),
          date: newCharge.date,
        },
      });
      setNewCharge({
        category: 'minibar',
        description: '',
        amount: '',
        date: format(new Date(), 'yyyy-MM-dd'),
      });
      setShowAddCharge(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add charge.');
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    try {
      await removeFolioItemMutation.mutateAsync({ itemId, bookingId: booking.id });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove charge.');
    }
  };

  const handleComplete = async () => {
    setError('');
    try {
      // Record payment if balance > 0
      if (balance > 0 && parseFloat(payment.amount) > 0) {
        const payPayload: Parameters<typeof recordPaymentMutation.mutateAsync>[0] = {
          bookingId: booking.id,
          amount: parseFloat(payment.amount),
          paymentMethod: payment.method,
        };
        if (payment.reference) payPayload.reference = payment.reference;
        if (payment.notes) payPayload.notes = payment.notes;
        await recordPaymentMutation.mutateAsync(payPayload);
      }

      // Check out
      const coPayload: Parameters<typeof checkOutMutation.mutateAsync>[0] = {
        id: booking.id,
        // Use noon of the selected date as actual checkout time so the date is unambiguous
        actualCheckOutTime: new Date(`${actualCheckOutDate}T12:00:00`).toISOString(),
      };
      if (checkoutNotes.trim()) coPayload.notes = checkoutNotes.trim();
      await checkOutMutation.mutateAsync(coPayload);

      // Automatically generate & open invoice preview on successful checkout
      setSuccessMessage('Guest checked out. Opening invoice…');
      try {
        await openBill(booking.id);
      } catch {
        // Non-fatal — checkout succeeded, invoice preview failed silently
      }
      setSuccessMessage('Guest checked out successfully. Invoice ready below.');
      // Don't auto-close — let staff click Close after confirming invoice
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed. Please try again.');
    }
  };

  const isProcessing = checkOutMutation.isPending || recordPaymentMutation.isPending;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
    <SlideOver isOpen={isOpen} onClose={onClose} title="Checkout Wizard" width="max-w-xl">
      <div>
        <StepIndicator steps={STEPS} currentStep={step} />

        {/* ─── Success screen ───────────────────────────────────────────────── */}
        {successMessage && (
          <div className="space-y-4">
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-start gap-3" role="status" aria-live="polite">
              <CheckCircle2 size={20} className="text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-green-800">Checkout complete</p>
                <p className="text-xs text-green-700 mt-0.5">{successMessage}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <PrintBillButton
                bookingId={booking.id}
                label="Print Invoice Again"
                className="flex-1"
              />
              <button
                type="button"
                onClick={onClose}
                className="flex-1 inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                Close
              </button>
            </div>
          </div>
        )}
        {!successMessage && error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3" role="alert">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* ─── Steps (hidden after successful checkout) ────────────────────── */}
        {!successMessage && step === 0 && (
          <div className="space-y-5">
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Receipt size={16} className="text-primary-600" aria-hidden="true" />
                  Folio Items
                </h3>
                <div className="flex items-center gap-2">
                  <PrintBillButton
                    bookingId={booking.id}
                    variant="inline"
                    label="Preview Bill"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowAddCharge(!showAddCharge)}
                  >
                    <Plus size={14} aria-hidden="true" />
                    Add Charge
                  </Button>
                </div>
              </div>

              {folioQuery.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="animate-spin text-primary-500" aria-label="Loading folio" />
                </div>
              ) : (
                <>
                  {/* Items list */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200" aria-label="Folio items">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Category</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Description</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Amount</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {folio?.items && folio.items.length > 0 ? (
                          folio.items.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-xs text-gray-600">{fmtDate(item.date)}</td>
                              <td className="px-3 py-2 text-xs text-gray-600 capitalize">{item.category}</td>
                              <td className="px-3 py-2 text-xs text-gray-900">{item.description}</td>
                              <td className="px-3 py-2 text-xs text-gray-900 text-right font-medium">
                                {fmtCurrency(item.amount, item.currency)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveItem(item.id)}
                                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                  aria-label={`Remove ${item.description}`}
                                  disabled={removeFolioItemMutation.isPending}
                                >
                                  <Trash2 size={14} aria-hidden="true" />
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-400">
                              No folio items yet. Room charges: {fmtCurrency(booking.totalAmount, currency)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Running total */}
                  <div className="flex justify-between items-center bg-gray-50 rounded-lg p-3 mt-3">
                    <span className="text-sm font-semibold text-gray-700">Total Charges</span>
                    <span className="text-sm font-bold text-gray-900">{fmtCurrency(totalCharges, currency)}</span>
                  </div>
                </>
              )}
            </section>

            {/* Add charge form */}
            {showAddCharge && (
              <section className="border border-gray-200 rounded-lg p-4 space-y-3 bg-blue-50/50">
                <h4 className="text-sm font-semibold text-gray-700">New Charge</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={chargeCategoryId} className="block text-xs font-medium text-gray-600 mb-1">
                      Category <span className="text-gray-400 font-normal">(required)</span>
                    </label>
                    <select
                      id={chargeCategoryId}
                      value={newCharge.category}
                      onChange={(e) => setNewCharge((prev) => ({ ...prev, category: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {CHARGE_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor={chargeDateId} className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                    <input
                      id={chargeDateId}
                      type="date"
                      value={newCharge.date}
                      onChange={(e) => setNewCharge((prev) => ({ ...prev, date: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor={chargeDescId} className="block text-xs font-medium text-gray-600 mb-1">
                    Description <span className="text-gray-400 font-normal">(required)</span>
                  </label>
                  <input
                    id={chargeDescId}
                    type="text"
                    value={newCharge.description}
                    onChange={(e) => setNewCharge((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g. Minibar - 2 soft drinks"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor={chargeAmountId} className="block text-xs font-medium text-gray-600 mb-1">
                    Amount <span className="text-gray-400 font-normal">(required)</span>
                  </label>
                  <input
                    id={chargeAmountId}
                    type="number"
                    min={0}
                    step="0.01"
                    value={newCharge.amount}
                    onChange={(e) => setNewCharge((prev) => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowAddCharge(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddCharge}
                    isLoading={addFolioItemMutation.isPending}
                    disabled={!newCharge.description.trim() || !newCharge.amount}
                  >
                    Add Charge
                  </Button>
                </div>
              </section>
            )}

            <div className="pt-2">
              <Button
                type="button"
                fullWidth
                onClick={() => { setError(''); setStep(1); }}
              >
                Continue to Payment
              </Button>
            </div>
          </div>
        )}

        {/* ─── Step 2: Payment Settlement ───────────────────────────────────── */}
        {!successMessage && step === 1 && (
          <div className="space-y-5">
            <section className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <CreditCard size={16} className="text-primary-600" aria-hidden="true" />
                Payment Summary
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Total Charges</dt>
                  <dd className="font-medium text-gray-900">{fmtCurrency(totalCharges, currency)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Total Paid</dt>
                  <dd className="font-medium text-green-700">{fmtCurrency(totalPayments, currency)}</dd>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-2">
                  <dt className="font-semibold text-gray-900">Outstanding Balance</dt>
                  <dd className={`font-bold ${balance > 0 ? 'text-red-600' : balance < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {balance < 0 ? `-${fmtCurrency(Math.abs(balance), currency)}` : fmtCurrency(balance, currency)}
                  </dd>
                </div>
              </dl>
            </section>

            {balance <= 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-600 shrink-0" aria-hidden="true" />
                <p className="text-sm font-medium text-green-700">
                  {balance < 0
                    ? `Overpaid by ${fmtCurrency(Math.abs(balance), currency)} -- refund may be needed.`
                    : 'Fully Settled'}
                </p>
              </div>
            ) : (
              <section className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700">Record Payment</h3>

                <div>
                  <label htmlFor={payAmountId} className="block text-sm font-medium text-gray-700 mb-1">
                    Amount <span className="text-gray-400 font-normal">(required)</span>
                  </label>
                  <input
                    id={payAmountId}
                    type="number"
                    min={0}
                    step="0.01"
                    value={payment.amount}
                    onChange={(e) => setPayment((prev) => ({ ...prev, amount: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor={payMethodId} className="block text-sm font-medium text-gray-700 mb-1">
                    Method <span className="text-gray-400 font-normal">(required)</span>
                  </label>
                  <select
                    id={payMethodId}
                    value={payment.method}
                    onChange={(e) => setPayment((prev) => ({ ...prev, method: e.target.value as PaymentMethodValue }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor={payRefId} className="block text-sm font-medium text-gray-700 mb-1">
                    Reference
                  </label>
                  <input
                    id={payRefId}
                    type="text"
                    value={payment.reference}
                    onChange={(e) => setPayment((prev) => ({ ...prev, reference: e.target.value }))}
                    placeholder="Receipt number, last 4 digits..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor={payNotesId} className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    id={payNotesId}
                    value={payment.notes}
                    onChange={(e) => setPayment((prev) => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    placeholder="Optional notes..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                </div>
              </section>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => setStep(0)}
              >
                Back
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={() => { setError(''); setStep(2); }}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* ─── Step 3: Confirm Checkout ─────────────────────────────────────── */}
        {!successMessage && step === 2 && (
          <div className="space-y-5">
            <section className="bg-gray-50 rounded-lg p-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-700">Checkout Summary</h3>

              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Guest</p>
                <p className="text-sm font-medium text-gray-900">{guestName}</p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Room</p>
                <p className="text-sm text-gray-700">
                  {booking.unit?.name ?? booking.unitId.slice(0, 8)}
                </p>
                <p className="text-xs text-gray-500">
                  {fmtDate(booking.checkIn)} - {fmtDate(booking.checkOut)}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Financial</p>
                <dl className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Total Charges</dt>
                    <dd className="font-medium">{fmtCurrency(totalCharges, currency)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Total Paid</dt>
                    <dd className="font-medium text-green-700">{fmtCurrency(totalPayments, currency)}</dd>
                  </div>
                  {balance > 0 && parseFloat(payment.amount) > 0 && (
                    <div className="flex justify-between text-primary-700">
                      <dt>Payment Now</dt>
                      <dd className="font-medium">{fmtCurrency(payment.amount, currency)}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </section>

            {/* Actual checkout date — supports early checkout */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Actual Check-out Date
                {actualCheckOutDate < booking.checkOut && (
                  <span className="ml-2 text-xs font-normal text-amber-600">
                    ⚡ Early checkout (scheduled: {fmtDate(booking.checkOut)})
                  </span>
                )}
              </label>
              <input
                type="date"
                value={actualCheckOutDate}
                min={booking.checkIn}
                max={booking.checkOut}
                onChange={(e) => setActualCheckOutDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-400">
                Set to an earlier date for early checkout. Defaults to today.
              </p>
            </div>

            <div>
              <label htmlFor={checkoutNotesId} className="block text-sm font-medium text-gray-700 mb-1">
                Checkout Notes
              </label>
              <textarea
                id={checkoutNotesId}
                value={checkoutNotes}
                onChange={(e) => setCheckoutNotes(e.target.value)}
                rows={3}
                placeholder="Any remarks about this checkout..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep(1)}
              >
                Back
              </Button>
              <Button
                type="button"
                fullWidth
                onClick={handleComplete}
                isLoading={isProcessing || isPrintingBill}
                disabled={isProcessing || isPrintingBill}
              >
                <CheckCircle2 size={16} aria-hidden="true" />
                Checkout & Print Invoice
              </Button>
            </div>
          </div>
        )}
      </div>
    </SlideOver>

    {/* Bill preview modal — outside SlideOver so it renders on top */}
    {billPreview && (
      <BillPreviewModal preview={billPreview} onClose={closePreview} />
    )}
    </>
  );
}
